import React from 'react';
import { Card, CardContent } from '../ui/card';
import { services } from '../../data/mock';
import { Code2, Sparkles, Zap, Rocket, RefreshCw } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/use-scroll-animation';

const iconMap = {
  Code2,
  Sparkles,
  Zap,
  Rocket,
  RefreshCw
};

const ServiceCard = ({ service, IconComponent, index }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <Card
      ref={ref}
      className={`group border-slate-200/80 hover:border-slate-300 hover:shadow-premium-xl transition-all duration-500 cursor-pointer bg-white/80 backdrop-blur-sm hover:bg-white hover:-translate-y-2 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <CardContent className="p-10">
        <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center mb-8 group-hover:from-slate-900 group-hover:to-slate-800 transition-all duration-500 shadow-premium group-hover:shadow-premium-lg group-hover:scale-110">
          {IconComponent && <IconComponent className="w-7 h-7 text-slate-600 group-hover:text-white transition-colors duration-500 z-10 relative" />}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-slate-900 transition-colors">
          {service.title}
        </h3>
        <p className="text-slate-600 leading-relaxed text-base font-medium">
          {service.description}
        </p>
      </CardContent>
    </Card>
  );
};

export const ServicesSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="services" className="py-32 lg:py-40 bg-white relative overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-50/30 to-transparent rounded-full blur-3xl opacity-40 animate-float-slow" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Premium Section Header */}
        <div 
          ref={headerRef}
          className={`max-w-3xl mb-20 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4 inline-block">What We Do</span>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
            Our Core Services
          </h2>
          <p className="mt-6 text-xl text-slate-600 leading-relaxed font-medium">
            Hands-on expertise from ideation to scale.
          </p>
        </div>

        {/* Premium Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <ServiceCard
                key={service.id}
                service={service}
                IconComponent={IconComponent}
                index={index}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};
